import {FC, MouseEvent, useState} from 'react';
import {VpnKeyOutlined} from '@mui/icons-material';
import {Box, Button, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {CredentialStatus} from '../CredentialStatus/CredentialStatus';
import {credentialName} from '../CompanyCredentialsTable.tableConfig';
import {CompanyCredentialItemCardProps} from '../CompanyCredentialsTable.types';
import {
    menuButtonSx,
    menuButtonTextSx,
    menuContentSx,
    popoverSx,
} from '../CompanyCredentialsTable.sx';

export const CompanyCredentialItemCard: FC<CompanyCredentialItemCardProps> = ({
                                                                                 credential,
                                                                                 onResend,
                                                                                 onDelete,
                                                                                 canManage,
                                                                             }) => {
    const intl = useIntl();
    // Below a desktop the menu is a sheet; from md up it stays the popover the sibling cards use.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const {id, emailAddress, created, passwordSetAt, lastLogin} = credential;
    const name = credentialName(credential);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // One node each, drawn by whichever of the two the width calls for, so the sheet and the popover
    // cannot come to offer different actions.
    const resendAction = (
        <Button
            sx={menuButtonSx}
            onClick={() => {
                handleClose();
                onResend(id);
            }}
        >
            <Typography sx={menuButtonTextSx}>
                {intl.formatMessage({id: 'resendInvitation'})}
            </Typography>
        </Button>
    );

    const deleteAction = (
        <Button
            sx={menuButtonSx}
            onClick={() => {
                handleClose();
                onDelete(id);
            }}
        >
            <Typography sx={menuButtonTextSx}>
                {intl.formatMessage({id: 'delete'})}
            </Typography>
        </Button>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<VpnKeyOutlined/>}
                person
                // The address is what a credential is; the name is what it is called, and it may be
                // missing, so the head falls back to the address rather than showing an empty line.
                title={name || emailAddress}
                subtitle={emailAddress}
                action={canManage ? <MenuButton handleClick={handleClick}/> : undefined}
            />
            <Box sx={phonePillsRowSx}>
                <CredentialStatus passwordSet={!!passwordSetAt}/>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'createdAt'}),
                        value: dayjs(created).format('DD/MM/YY'),
                    },
                    {
                        label: intl.formatMessage({id: 'passwordSetAt'}),
                        value: passwordSetAt ? dayjs(passwordSetAt).format('DD/MM/YY') : '—',
                    },
                    {
                        label: intl.formatMessage({id: 'lastLogin'}),
                        value: lastLogin ? dayjs(lastLogin).format('DD/MM/YY') : '—',
                    },
                ]}
            />
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<VpnKeyOutlined/>}
                    title={name || emailAddress}
                    subtitle={emailAddress}
                >
                    {resendAction}
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={menuContentSx}>
                        {resendAction}
                        {deleteAction}
                    </Box>
                </Popover>
            )}
        </TableCard>
    );
};
